import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { 
  RegistrationFormData, 
  RegistrationResponse, 
  Document, 
  Pet, 
  DashboardData, 
  Property,
  ApiError 
} from '../types';

const resolveApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
    return 'http://localhost:8000/api/';
  }
  return import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/';
};

const API_BASE_URL = resolveApiBaseUrl();
console.log('[petService] API base URL:', API_BASE_URL);

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*', 
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('⚠️ No auth token found in localStorage');
      }
    } catch (error) {
      console.error('❌ Error getting auth token:', error);
    }
    try {
      const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
      console.log('[petService] Request:', (config.method || 'get').toUpperCase(), fullUrl);
    } catch {}
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: any) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access - clear stale auth data and redirect.
      // Send to /login with a hint so the user sees a friendly "Session
      // expired, please sign in again" notice instead of a silent bounce.
      console.error('Unauthorized access - clearing auth state and redirecting to login');
      localStorage.removeItem('authToken');
      localStorage.removeItem('totpVerified');
      localStorage.removeItem('totpVerifiedAt');
      localStorage.removeItem('totpSetupAt');
      sessionStorage.removeItem('sessionActive');
      window.location.href = '/login?hint=session-expired';
    }
    // Network/connectivity fallback: if localhost API is not reachable, switch to remote and retry once
    try {
      const isNetworkError = !error.response;
      const usedLocalhost = (apiClient.defaults.baseURL || '').includes('localhost:8000');
      const alreadyRetried = error.config && error.config._retriedWithRemote;
      const remoteBase = import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/';
      if (isNetworkError && usedLocalhost && !alreadyRetried) {
        console.warn('[petService] Local API not reachable. Switching to remote API base.');
        apiClient.defaults.baseURL = remoteBase;
        const newConfig = { ...error.config, baseURL: remoteBase, _retriedWithRemote: true };
        console.log('[petService] Retrying:', (newConfig.method || 'get').toUpperCase(), `${newConfig.baseURL || ''}${newConfig.url || ''}`);
        return apiClient.request(newConfig);
      }
    } catch {}
    return Promise.reject(error);
  }
);

// Refresh access token (extends session while user is active)
export const refreshToken = async (): Promise<string> => {
  const response = await apiClient.post('/auth/refresh');
  const newToken = response.data.access_token;
  localStorage.setItem('authToken', newToken);
  return newToken;
};

// Helper function to convert files to FormData for S3 upload
const prepareDocumentsForUpload = (documents: Document[]): FormData => {
  const formData = new FormData();
  
  documents.forEach((doc, index) => {
    if (doc.fileData) {
      // Convert base64 to blob
      const byteCharacters = atob(doc.fileData.split(',')[1] || doc.fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.mimeType });
      
      // Create File object with original name
      const file = new File([blob], doc.name, { type: doc.mimeType });
      formData.append('files', file);
    }
  });
  
  return formData;
};

// Pet Registration Service
export const petService = {
  /** Finalize registration — metadata only. Files must already be on S3 via upload-as-you-go. */
  registerPets: async (formData: RegistrationFormData): Promise<RegistrationResponse> => {
    try {
      console.log('Starting pet registration process with form data:', formData);
      
      const registrationResults = [];
      const errors = [];

      // Register each pet individually
      for (let i = 0; i < formData.pets.length; i++) {
        const pet = formData.pets[i];
        
        try {
          console.log(`Registering pet ${i + 1}/${formData.pets.length}: ${pet.petName}`);

          // Transform frontend data to match backend schema for each pet.
          // Documents / pet photos are uploaded earlier via presigned S3 PUT
          // (or multipart fallback) — keep this payload small for mobile.
          const payload = {
            property_info: {
              property: formData.property,
              unit: formData.unit,
              contact_email: formData.contactEmail,
              contact_phone: formData.contactPhone,
            },
            pet_details: {
              pet_name: pet.petName,
              pet_type: pet.petType === 'other' ? (pet.customPetType || pet.petType || 'other') : (pet.petType || 'dog'),
              breed: pet.breed === 'Other' ? (pet.customBreed || 'Other') : (pet.breed || ''),
              age: pet.age || 0,
              weight: typeof pet.weight === 'object' ? (pet.weight.value || 0) : (parseFloat(String(pet.weight)) || 0),
              color: pet.color === 'Other' ? (pet.customColor || 'Other') : (pet.color || null),
              markings: pet.markings === 'Other' ? (pet.customMarkings || 'Other') : (pet.markings || null),
              gender: pet.gender || null,
              move_in_date: pet.move_in_date || null,
              move_out_date: pet.move_out_date || null,
              spayed_neutered: pet.spayed_neutered,
              previous_pet_ownership: pet.previous_pet_ownership ?? null,
            },
            veterinary_info: {
              vet_clinic: pet.vetClinic,
              vet_phone: pet.vetPhone,
              vet_address: pet.vetAddress || null,
              last_visit: pet.lastVisit || null,
            },
            esa_info: {
              pet_role: pet.animalType || 'pet',
              esa_professional: pet.esaProfessional || null,
              esa_letter_date: pet.esaLetterDate || null,
              esa_registration: pet.esaProviderLicenseNumber || pet.esaRegistration || null,
            },
            documents: [],
            attestation: formData.attestation || null,
            ...(formData.draftPetId ? { draft_pet_id: formData.draftPetId } : {}),
            ...(pet.customFields && Object.keys(pet.customFields).length > 0
              ? { custom_fields: pet.customFields } : {}),
          };

          console.log(`Sending registration payload for pet ${i + 1}:`, {
            ...payload,
            documents: [],
          });

          const response = await apiClient.post('pets/register', payload);
          const registeredPetId = response.data.id;
          
          console.log(`✅ Pet registered successfully: ${pet.petName} with ID: ${registeredPetId}`);

          const documentsProcessed = (pet.documents || []).filter(
            (d: any) => d?.uploadStatus === 'success' || d?.restoredFromServer,
          ).length;
          const photoUploaded = Boolean(
            response.data.image_url ||
              pet.photoUploadStatus === 'success' ||
              pet.photos?.[0]?.uploadStatus === 'success',
          );
          
          registrationResults.push({
            petIndex: i,
            petName: pet.petName,
            success: true,
            data: response.data,
            petId: registeredPetId,
            documentsCount: documentsProcessed,
            photoUploaded,
            photoAttempted: Boolean(
              pet.photoFile ||
                (Array.isArray(pet.photos) && pet.photos.some((p) => p?.file)),
            ),
            s3Folder: `s3://prod-pawtection-input-bucket/${registeredPetId}/`
          });
          
        } catch (petError: any) {
          console.error(`❌ Error registering pet ${i + 1} (${pet.petName}):`, petError);
          
          let errorMessage = `Failed to register ${pet.petName}: `;
          
          if (petError.response) {
            console.error('Error response:', petError.response.data);
            console.error('Error status:', petError.response.status);
            
            if (petError.response.status === 422 && petError.response.data.detail) {
              const validationErrors = petError.response.data.detail;
              console.error('Validation errors:', validationErrors);
              
              if (Array.isArray(validationErrors)) {
                const fieldErrors = validationErrors.map(err => {
                  const field = err.loc ? err.loc[err.loc.length - 1] : 'unknown';
                  return `${field}: ${err.msg}`;
                });
                errorMessage += fieldErrors.join(', ');
              } else {
                errorMessage += validationErrors;
              }
            } else {
              const detail = petError.response.data?.detail;
              const serverReason =
                typeof detail === 'string' && detail.trim()
                  ? detail.trim()
                  : petError.response.data?.message;
              errorMessage += serverReason || petError.message;
            }
          } else {
            errorMessage += petError.message;
          }
          
          errors.push({
            petIndex: i,
            petName: pet.petName,
            error: errorMessage
          });
        }
      }

      // Return results summary
      const result = {
        totalPets: formData.pets.length,
        successful: registrationResults.length,
        failed: errors.length,
        results: registrationResults,
        errors: errors
      };

      console.log('🎉 Registration process completed:', result);

      // If some registrations failed, throw an error with details
      if (errors.length > 0) {
        const errorSummary = errors.map(err => err.error).join('\n');
        const successSummary = registrationResults.length > 0 
          ? `\n\n✅ ${registrationResults.length} pets were successfully registered.`
          : '';
        
        throw new Error(`Registration issues:\n${errorSummary}${successSummary}`);
      }

      return result;
      
    } catch (error) {
      console.error('❌ Pet registration error:', error);
      throw error;
    }
  },

  requestUploadPresign: async (body: {
    pet_id: number;
    upload_type: 'document' | 'pet_photo';
    file_name: string;
    content_type: string;
    content_length: number;
    document_type?: string;
    document_name?: string;
  }): Promise<{
    presigned_url: string;
    s3_key: string;
    bucket: string;
    method: string;
    headers: Record<string, string>;
    expires_in: number;
  }> => {
    const response = await apiClient.post('uploads/presign', body);
    return response.data;
  },

  completeUpload: async (body: {
    pet_id: number;
    upload_type: 'document' | 'pet_photo';
    s3_key: string;
    file_name: string;
    content_type: string;
    content_length: number;
    content_hash?: string;
    document_type?: string;
    document_name?: string;
  }): Promise<any> => {
    const response = await apiClient.post('uploads/complete', body);
    return response.data;
  },

  // Get documents for a specific pet
  getPetDocuments: async (petId: number): Promise<Document[]> => {
    try {
      const response = await apiClient.get(`pets/${petId}/documents`);
      // Return the documents array from the response
      return response.data.documents || [];
    } catch (error) {
      console.error('Error fetching pet documents:', error);
      throw error;
    }
  },

  // Get all documents for the current user
  getUserDocuments: async (): Promise<Document[]> => {
    try {
      const response = await apiClient.get('documents/user');
      return response.data;
    } catch (error) {
      console.error('Error fetching user documents:', error);
      throw error;
    }
  },

  // Upload additional documents for an existing pet
  uploadPetDocuments: async (petId: number, files: File[], docType: string | null = null, docName: string | null = null): Promise<any> => {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add document type metadata if provided
      if (docType) {
        formData.append('document_type', docType);
      }
      if (docName) {
        formData.append('document_name', docName);
      }

      const response = await apiClient.post(`documents/upload?pet_id=${petId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log(`Successfully uploaded ${files.length} documents for pet ${petId}`);
      return response.data;
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  },

  // Associate documents (base64) with an existing pet via the backend endpoint
  associateDocuments: async (petId: number, documents: Array<{
    name: string;
    type: string;
    mimeType: string;
    size: number;
    fileData: string | null;
    petIndex?: number;
    petNumber?: number;
  }>): Promise<any> => {
    try {
      const response = await apiClient.post(`pets/${petId}/documents`, { documents });
      return response.data;
    } catch (error) {
      console.error('Error associating documents:', error);
      throw error;
    }
  },

  // Delete a specific document
  deleteDocument: async (documentId: number): Promise<any> => {
    try {
      const response = await apiClient.delete(`documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // Get all pets for current user
  getUserPets: async (): Promise<Pet[]> => {
    try {
      const response = await apiClient.get('pets');
      return response.data;
    } catch (error) {
      console.error('Error fetching pets:', error);
      throw error;
    }
  },

  // Snapshot of the per-lease pet cap: how many pets the caller's lease
  // currently holds vs how many it can hold. Backed by
  // GET /api/pets/lease-summary — the same source the backend uses to
  // enforce the cap on register/draft.
  getLeasePetSummary: async (): Promise<{
    lease_id: number | null;
    partition: 'lease' | 'user';
    active_pet_count: number;
    max_allowed: number;
    remaining: number;
  }> => {
    const response = await apiClient.get('pets/lease-summary');
    return response.data;
  },

  // Get specific pet details
  getPetDetails: async (petId: number): Promise<Pet> => {
    try {
      const response = await apiClient.get(`pets/${petId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pet details:', error);
      throw error;
    }
  },

  // Update pet information
  updatePet: async (petId: number, updateData: Partial<Pet>): Promise<Pet> => {
    try {
      const response = await apiClient.put(`pets/${petId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating pet:', error);
      throw error;
    }
  },

  // Deactivate a pet (set status to inactive)
  deactivatePet: async (petId: number, reason: string, moveOutDate: string, notes?: string): Promise<any> => {
    try {
      const response = await apiClient.patch(`pets/${petId}/deactivate`, { 
        reason, 
        move_out_date: moveOutDate,
        notes 
      });
      console.log(`Pet ${petId} deactivated successfully`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating pet:', error);
      throw error;
    }
  },

  // Delete a pet
  deletePet: async (petId: number): Promise<any> => {
    try {
      const response = await apiClient.delete(`pets/${petId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting pet:', error);
      throw error;
    }
  },

  // Upload animal images (photo) for a pet
  uploadAnimalImages: async (petId: number, files: File[]): Promise<any> => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const response = await apiClient.post(`animal-images/upload?pet_id=${petId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading animal images:', error);
      throw error;
    }
  },

  deletePetImage: async (petId: number): Promise<any> => {
    try {
      const response = await apiClient.delete(`pets/${petId}/image`);
      return response.data;
    } catch (error) {
      console.error('Error deleting pet image:', error);
      throw error;
    }
  },

  // Get all images for a pet
  getPetImages: async (petId: number): Promise<any> => {
    try {
      const response = await apiClient.get(`pets/${petId}/images`);
      return response.data;
    } catch (error) {
      console.error('Error getting pet images:', error);
      throw error;
    }
  },

  // Get dashboard data
  getDashboardData: async (): Promise<DashboardData> => {
    try {
      const response = await apiClient.get('dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Get list of properties for dropdowns
  getPropertiesList: async (): Promise<Property[]> => {
    try {
      const response = await apiClient.get('properties/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching properties list:', error);
      // Return empty array on error
      return [];
    }
  },

  // Test AWS credentials (for debugging)
  testAWSCredentials: async (): Promise<any> => {
    try {
      const response = await apiClient.get('test/aws-credentials');
      return response.data;
    } catch (error) {
      console.error('Error testing AWS credentials:', error);
      throw error;
    }
  },

  // Update user profile (e.g., name, phone)
  updateUserProfile: async (updates: Partial<{ name: string; phone: string; contact_phone: string; unit: string; }>): Promise<any> => {
    try {
      const response = await apiClient.put('users/profile', updates);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  /** Set has_pets to true when a returning no-pet user decides to register a pet. */
  setHasPets: async (): Promise<{ message: string; has_pets: boolean }> => {
    try {
      const response = await apiClient.post('users/declare-has-pets');
      return response.data;
    } catch (error) {
      console.error('Error setting has_pets:', error);
      throw error;
    }
  },

  saveDraft: async (draftData: {
    property_info?: Record<string, any>;
    pet_details?: Record<string, any>;
    veterinary_info?: Record<string, any>;
    esa_info?: Record<string, any>;
    custom_fields?: Record<string, string>;
    draft_step?: number;
  }): Promise<any> => {
    try {
      const response = await apiClient.post('pets/draft', draftData);
      return response.data;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  },

  updateDraft: async (petId: number, draftData: {
    property_info?: Record<string, any>;
    pet_details?: Record<string, any>;
    veterinary_info?: Record<string, any>;
    esa_info?: Record<string, any>;
    custom_fields?: Record<string, string>;
    draft_step?: number;
  }): Promise<any> => {
    try {
      const response = await apiClient.put(`pets/${petId}/draft`, draftData);
      return response.data;
    } catch (error) {
      console.error('Error updating draft:', error);
      throw error;
    }
  },

  /**
   * Fire-and-forget draft save for use during page unload (beforeunload).
   * Uses fetch + keepalive so the browser finishes the request even after
   * the page is torn down. Returns void — callers cannot await a result.
   */
  saveDraftBeacon: (draftData: {
    property_info?: Record<string, any>;
    pet_details?: Record<string, any>;
    veterinary_info?: Record<string, any>;
    esa_info?: Record<string, any>;
    custom_fields?: Record<string, string>;
    draft_step?: number;
  }, existingDraftId?: number): void => {
    try {
      const token = localStorage.getItem('authToken');
      const base = apiClient.defaults.baseURL || API_BASE_URL;
      const url = existingDraftId
        ? `${base}pets/${existingDraftId}/draft`
        : `${base}pets/draft`;
      fetch(url, {
        method: existingDraftId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(draftData),
        keepalive: true,
      });
    } catch {
      // Best-effort — nothing we can do if this fails during unload
    }
  },

  getPaymentStatus: async (petId: number): Promise<{ payment_status: string }> => {
    try {
      const response = await apiClient.get(`stripe/status/${petId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment status:', error);
      throw error;
    }
  },

  sendPaymentConfirmationEmail: async (petId: number): Promise<{ status: string }> => {
    try {
      const response = await apiClient.post(`pets/send-registration-email?pet_id=${petId}`);
      return response.data;
    } catch (error) {
      console.error('Error sending payment confirmation email:', error);
      throw error;
    }
  },

  /** Submit no-pet declaration (sets has_pets to false on backend). */
  submitNoPetDeclaration: async (data: { phone: string; attestation: { attested_at: string; attestation_text: string; signature?: string } }): Promise<{ message: string; has_pets: boolean }> => {
    try {
      const response = await apiClient.post('users/no-pet-declaration', {
        phone: data.phone,
        attestation: data.attestation,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting no-pet declaration:', error);
      throw error;
    }
  },
};